import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { TripCard } from "@/components/TripCard";
import { Marquee } from "@/components/Marquee";

const GROUPS = [
  { key: "coming_soon", label: "Coming Soon", sub: "Launch trips — final dates are being locked in. Waitlist sees them first." },
  { key: "waitlist", label: "Waitlist Open", sub: "Waitlist-only for now. Dates, pricing and booking details release to the list first." },
  { key: "future", label: "Future Adventures — 2027 & Beyond", sub: "Concepts and future destinations. Vote for what you want next — votes genuinely shape what we plan." },
];

export default function TripsPage() {
  useSEO("All Trips — Roamies", "Small-group trips for solo travelers ages 18–45. Coming soon drops, waitlist trips, and future adventures you can vote on.");
  const [trips, setTrips] = useState([]);
  const [votes, setVotes] = useState({});
  const [voted, setVoted] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/trips").then((r) => setTrips(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/votes").then((r) => setVotes(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  const vote = async (slug) => {
    if (voted[slug]) return;
    setVoted((v) => ({ ...v, [slug]: true }));
    setVotes((v) => ({ ...v, [slug]: (v[slug] || 0) + 1 }));
    try {
      const { data } = await api.post("/votes", { trip_slug: slug });
      setVotes((v) => ({ ...v, [slug]: data.votes }));
    } catch {
      toast.error("Vote didn't save — try again.");
    }
  };

  return (
    <div data-testid="trips-page">
      <section className="pt-16 sm:pt-24 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="text-xs font-bold tracking-[0.2em] uppercase text-flame mb-4">
            All Trips · Max 12 travelers · Ages 18–45
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="font-display text-5xl sm:text-6xl md:text-7xl uppercase leading-[0.9]"
            data-testid="trips-page-heading">
            Where are we<br />going?
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}
            className="mt-5 max-w-xl text-ink/70 leading-relaxed">
            Dates drop to the waitlist first — nothing here takes payment yet. Join a list, vote on a concept, help us pick what launches next.
          </motion.p>
        </div>
      </section>

      {loading ? (
        <p className="max-w-7xl mx-auto px-4 sm:px-6 text-ink/50 font-semibold py-20" data-testid="trips-loading">Loading trips...</p>
      ) : (
        GROUPS.map((group) => {
          const list = trips.filter((t) => (t.collection || "future") === group.key);
          if (list.length === 0) return null;
          return (
            <section key={group.key} className="pb-16" data-testid={`trips-group-${group.key}`}>
              <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <div className="border-t-2 border-ink pt-8 mb-8">
                  <h2 className="font-display text-3xl sm:text-4xl uppercase">{group.label}</h2>
                  <p className="mt-2 text-sm text-ink/60 max-w-xl">{group.sub}</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {list.map((trip, i) => (
                    <motion.div key={trip.slug}
                      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: i * 0.06 }}>
                      <TripCard trip={trip} showVote={group.key === "future"} voteCount={votes[trip.slug]} voted={voted[trip.slug]} onVote={vote} />
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>
          );
        })
      )}

      <Marquee />
    </div>
  );
}
